import { faArrowLeft, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Badge, Button, Card, Col, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import AdminServiceV2 from "../../../../services/admin.service.v2";
import ConfirmModal from "../../../../components/Modal/Confirm.modal";

interface ICounsellingUser {
    _id: string;
    rollNo: string;
    applicationNumber: string;
    capApplicationNumber: string;
    name: string;
    allotedInstituteCode: string;
    allotedInstituteName: string;
    allotedInstituteAddress: string;
    round: string;
    stream: string;
    examId: string;
    createdAt: string;
    updatedAt: string;
    userId: {
        _id: string;
        rollNo: string;
        shiftId: string;
        examId: string;
        name: string;
        regNo: string;
    };
}

interface IVerification {
    _id: string;
    studentId: {
        _id: string;
        rollNo: string;
        shiftId: string;
        examId: string;
        name: string;
        regNo: string;
    };
    examId: string;
    shiftId: string;
    verificationId: string;
    userLivePhoto: string;
    createdAt: string;
}

interface IStudent {
    _id: string;
    rollNo: string;
    shiftId: string;
    examId: string;
    name: string;
    regNo: string;
    avatar: string;
}

interface IReverification {
    _id: string;
    verificationId: string;
    studentId: string;
    examId: string;
    method: string;
    facialStatus: string;
    fingerprintStatus: string;
    success: boolean;
    manuallyVerified: boolean;
    consent: string;
    collegeName: string;
    collegId: string;
    createdAt: string;
}

interface ISearchResult {
    counsellingUser: ICounsellingUser | any;
    verification: IVerification[];
    student: IStudent[];
    reverification: IReverification[];
}

const ReverificationSearch = () => {
    const params = useParams();
    const navigate = useNavigate();

    const [query, setQuery] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<ISearchResult | null>(null);
    const [searched, setSearched] = useState<boolean>(false);
    const [remappingId, setRemappingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<IReverification | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    const handleSearch = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setResult(null);
        setSearched(false);
        try {
            const res = await AdminServiceV2.searchReverificationUser(params.examId!, query.trim());
            if (res.status === 200) {
                setResult(res.data?.data || null);
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "No user found");
        } finally {
            setLoading(false);
            setSearched(true);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") handleSearch();
    };

    const handleRemap = async (mapToUserId: string) => {
        if (!result?.counsellingUser?._id) return;
        setRemappingId(mapToUserId);
        try {
            const res = await AdminServiceV2.remapReverificationUser(result.counsellingUser._id, mapToUserId);
            if (res.status === 200) {
                toast.success("Counselling user remapped successfully");
                handleSearch();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to remap user");
        } finally {
            setRemappingId(null);
        }
    };

    const handleDeleteReverification = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const shiftId = result?.verification?.[0]?.shiftId || "";
            const res = await AdminServiceV2.deleteReverification(
                params.examId!,
                deleteTarget.studentId,
                deleteTarget._id,
                shiftId
            );
            if (res.status === 200) {
                toast.success("Reverification deleted successfully");
                setDeleteTarget(null);
                handleSearch();
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to delete reverification");
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (iso: string) =>
        iso ? new Date(iso).toLocaleString() : "—";

    return (
        <Card className="bio-card mt-4">
            <Card.Body>
                <div className="d-flex align-items-center mb-3">
                    <Button
                        variant="link"
                        className="p-0 me-3 text-muted"
                        onClick={() => navigate(`/dashboard/exam/${params.examId}`)}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                    <h6 className="text-muted fw-bold mb-0">Reverification User Search</h6>
                </div>
                <hr className="text-muted" />

                <div style={{ maxWidth: 560 }}>
                    <InputGroup>
                        <Form.Control
                            placeholder="Enter CAP Application Number..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="xcn-text-12"
                        />
                        <Button
                            variant="primary"
                            onClick={handleSearch}
                            disabled={loading || !query.trim()}
                        >
                            {loading ? (
                                <Spinner size="sm" animation="border" />
                            ) : (
                                <FontAwesomeIcon icon={faSearch} />
                            )}
                        </Button>
                    </InputGroup>
                    <small className="text-muted xcn-text-12">Search by CAP Application Number</small>
                </div>

                {searched && !result && (
                    <p className="text-muted xcn-text-12 mt-4">No user found for "{query}".</p>
                )}

                {result && (
                    <div className="mt-4">

                        {/* Counselling User */}
                        <h6 className="fw-bold text-secondary xcn-text-12 mb-2">Counselling Details</h6>
                        <Card className="mb-4 border">
                            <Card.Body className="xcn-text-12">
                                <Row>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Counselling ID</span>
                                        <span style={{ fontFamily: "monospace", fontSize: 11 }}>{result.counsellingUser?._id || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Current User ID</span>
                                        <span style={{ fontFamily: "monospace", fontSize: 11 }}>{result.counsellingUser?.userId?._id || result.counsellingUser?.userId || "-"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Name</span>
                                        <span>{result.counsellingUser?.name || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Roll No</span>
                                        <span>{result.counsellingUser?.rollNo || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">CAP Application No</span>
                                        <span>{result.counsellingUser?.capApplicationNumber || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Application No</span>
                                        <span>{result.counsellingUser?.applicationNumber || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Alloted Institute</span>
                                        <span>{result.counsellingUser?.allotedInstituteName || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Institute Code</span>
                                        <span>{result.counsellingUser?.allotedInstituteCode || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Stream</span>
                                        <span>{result.counsellingUser?.stream || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Round</span>
                                        <span>{result.counsellingUser?.round || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Shift ID</span>
                                        <span style={{ fontFamily: "monospace", fontSize: 11 }}>{result.counsellingUser?.userId?.shiftId || "—"}</span>
                                    </Col>
                                    <Col md={4} className="mb-2">
                                        <span className="text-muted fw-bold d-block">Created At</span>
                                        <span>{formatDate(result.counsellingUser?.createdAt)}</span>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Verifications */}
                        <h6 className="fw-bold text-secondary xcn-text-12 mb-2">
                            Verifications
                            <Badge bg="primary" className="ms-2">{result.verification?.length || 0}</Badge>
                        </h6>
                        {result.verification?.length > 0 ? (
                            <Table striped hover className="xcn-text-12 mb-4">
                                <thead>
                                    <tr>
                                        <th className="text-muted fw-bold">Live Photo</th>
                                        <th className="text-muted fw-bold">Verification ID</th>
                                        <th className="text-muted fw-bold">Student ID</th>
                                        <th className="text-muted fw-bold">Roll No</th>
                                        <th className="text-muted fw-bold">Reg No</th>
                                        <th className="text-muted fw-bold">Shift ID</th>
                                        <th className="text-muted fw-bold">Created At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.verification.map((v) => (
                                        <tr key={v._id}>
                                            <td>
                                                {v.userLivePhoto ? (
                                                    <img
                                                        src={v.userLivePhoto}
                                                        alt="live"
                                                        style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }}
                                                    />
                                                ) : "—"}
                                            </td>
                                            <td>{v.verificationId}</td>
                                            <td className="text-muted" style={{ fontFamily: "monospace", fontSize: 11 }}>{v.studentId?._id}</td>
                                            <td>{v.studentId?.rollNo}</td>
                                            <td>{v.studentId?.regNo}</td>
                                            <td className="text-muted" style={{ fontFamily: "monospace", fontSize: 11 }}>{v.shiftId}</td>
                                            <td>{formatDate(v.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted xcn-text-12 mb-4">No verifications found.</p>
                        )}

                        {/* Students */}
                        <h6 className="fw-bold text-secondary xcn-text-12 mb-2">
                            Student Records
                            <Badge bg="secondary" className="ms-2">{result.student?.length || 0}</Badge>
                        </h6>
                        {result.student?.length > 0 ? (
                            <Table striped hover className="xcn-text-12">
                                <thead>
                                    <tr>
                                        <th className="text-muted fw-bold">Avatar</th>
                                        <th className="text-muted fw-bold">Student ID</th>
                                        <th className="text-muted fw-bold">Name</th>
                                        <th className="text-muted fw-bold">Roll No</th>
                                        <th className="text-muted fw-bold">Reg No</th>
                                        <th className="text-muted fw-bold">Shift ID</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.student.map((s) => (
                                        <tr key={s._id}>
                                            <td>
                                                {s.avatar ? (
                                                    <img
                                                        src={s.avatar}
                                                        alt="avatar"
                                                        style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }}
                                                    />
                                                ) : "—"}
                                            </td>
                                            <td className="text-muted" style={{ fontFamily: "monospace", fontSize: 11 }}>{s._id}</td>
                                            <td>{s.name}</td>
                                            <td>{s.rollNo}</td>
                                            <td>{s.regNo}</td>
                                            <td className="text-muted" style={{ fontFamily: "monospace", fontSize: 11 }}>{s.shiftId}</td>
                                            <td>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="fw-bold xcn-text-12 text-nowrap"
                                                    disabled={remappingId === s._id}
                                                    onClick={() => handleRemap(s._id)}
                                                >
                                                    {remappingId === s._id ? (
                                                        <Spinner size="sm" animation="border" />
                                                    ) : "Remap Counselling User"}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted xcn-text-12">No student records found.</p>
                        )}

                        {/* Reverification Logs */}
                        {result.reverification?.length > 0 && (
                            <>
                                <h6 className="fw-bold text-secondary xcn-text-12 mb-2 mt-4">
                                    Reverification Logs
                                    <Badge bg="info" className="ms-2">{result.reverification.length}</Badge>
                                </h6>
                                <Table striped hover className="xcn-text-12">
                                    <thead>
                                        <tr>
                                            <th className="text-muted fw-bold">Method</th>
                                            <th className="text-muted fw-bold">Facial Status</th>
                                            <th className="text-muted fw-bold">Fingerprint Status</th>
                                            <th className="text-muted fw-bold">Consent</th>
                                            <th className="text-muted fw-bold">Success</th>
                                            <th className="text-muted fw-bold">Manual</th>
                                            <th className="text-muted fw-bold">College</th>
                                            <th className="text-muted fw-bold">College ID</th>
                                            <th className="text-muted fw-bold">Verification ID</th>
                                            <th className="text-muted fw-bold">Record ID</th>
                                            <th className="text-muted fw-bold">Created At</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.reverification.map((r) => (
                                            <tr key={r._id}>
                                                <td className="text-capitalize">{r.method || "—"}</td>
                                                <td>
                                                    <Badge bg={r.facialStatus === "matched" ? "success" : "danger"} className="text-capitalize">
                                                        {r.facialStatus || "—"}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge bg={r.fingerprintStatus === "matched" ? "success" : "danger"} className="text-capitalize">
                                                        {r.fingerprintStatus || "—"}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge bg={r.consent === "matched" ? "success" : "warning"} className="text-capitalize">
                                                        {r.consent || "—"}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge bg={r.success ? "success" : "danger"}>
                                                        {r.success ? "Yes" : "No"}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge bg={r.manuallyVerified ? "warning" : "secondary"}>
                                                        {r.manuallyVerified ? "Yes" : "No"}
                                                    </Badge>
                                                </td>
                                                <td>{r.collegeName || "—"}</td>
                                                <td>{r.collegId || "—"}</td>
                                                <td className="text-muted" style={{ fontFamily: "monospace", fontSize: 11 }}>{r.verificationId}</td>
                                                <td className="text-muted" style={{ fontFamily: "monospace", fontSize: 11 }}>{r._id}</td>
                                                <td>{formatDate(r.createdAt)}</td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="fw-bold xcn-text-12 text-nowrap"
                                                        onClick={() => setDeleteTarget(r)}
                                                    >
                                                        Delete this Reverification
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}

                    </div>
                )}
            </Card.Body>

            <ConfirmModal
                titleText={<span className="fw-bold text-danger">Delete Reverification</span>}
                bodyText={
                    <div>
                        <div className="alert alert-danger xcn-text-12 mb-2">
                            ⚠️ This can't be undone. Are you sure you want to proceed?
                        </div>
                        <p className="xcn-text-12 mb-0">
                            Record ID: <span style={{ fontFamily: "monospace" }}>{deleteTarget?._id}</span>
                        </p>
                    </div>
                }
                show={!!deleteTarget}
                onHide={() => setDeleteTarget(null)}
                button={[
                    {
                        label: "Cancel",
                        action: () => setDeleteTarget(null),
                        variant: "secondary",
                    },
                    {
                        label: deleting ? <Spinner size="sm" animation="border" /> : "Delete this Reverification",
                        action: handleDeleteReverification,
                        variant: "danger",
                    },
                ]}
            />
        </Card>
    );
};

export default ReverificationSearch;
